import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Flex, Input, Text } from '@nula/core-ui';
import { useValidatePromoCode } from './use-subscription';
import { correctedDiscountDescription } from './discount-description';
import { PENDING_REFERRAL_CODE_KEY } from './pending-referral';
import type { BillingInterval, PromoRejectReason } from './types';
import styles from './promo-code-input.module.css';

export interface AppliedPromo {
  code: string;
  description: string | null;
  percentOff: number | null;
  amountOffCents: number | null;
}

export interface CheckoutRejection {
  /** Distinguishes two rejections carrying the same reason (e.g. retrying the same code twice). */
  nonce: number;
  reason: PromoRejectReason;
}

interface PromoCodeInputProps {
  interval: BillingInterval;
  workspaceId?: string;
  onApplied: (promo: AppliedPromo) => void;
  onRemoved: () => void;
  /**
   * Set by the parent when checkout itself (not preview validation) rejects
   * the applied code — Stripe's second validation at subscription-create can
   * reject a code that passed preview (e.g. deactivated in between). Clears
   * the applied badge and struck price rather than leaving them asserting a
   * discount the server just refused.
   */
  checkoutRejection?: CheckoutRejection | null;
  /**
   * When true, a code that validates as a referral is rejected client-side
   * instead of applied. Set by callers that have no workspace to check the
   * personal-only rule against (team creation checks out against a
   * workspace that doesn't exist yet) — see `ValidatePromoResponse.isReferral`.
   */
  rejectReferrals?: boolean;
}

/**
 * Shown when validation itself fails (non-2xx, network error) — distinct from
 * REJECTION_MESSAGE, which is for a code the server actually evaluated and
 * refused. An infra failure carries no verdict on the code, so reusing a
 * rejection reason here would misrepresent the code as invalid.
 */
const VALIDATION_FAILED_MESSAGE = "Couldn't check that code. Please try again.";

/**
 * The endpoint deliberately collapses unknown, inactive and expired codes into
 * one reason so it can't be used to probe which codes exist — this map keeps
 * that collapse in the copy rather than inventing distinct messages.
 */
const REJECTION_MESSAGE: Record<PromoRejectReason, string> = {
  wrong_interval: 'Referral codes work on monthly plans. Switch to monthly to use this code.',
  self_referral: 'This is your own referral code. Share it with someone else to earn credit.',
  already_redeemed: 'This workspace has already used a discount code.',
  referral_team_ineligible: 'Referral codes are for personal plans.',
  stripe_rejected: "That code isn't valid. Check for typos and try again.",
  unknown_code: "That code isn't valid. Check for typos and try again.",
};

export function PromoCodeInput({
  interval,
  workspaceId,
  onApplied,
  onRemoved,
  checkoutRejection,
  rejectReferrals,
}: PromoCodeInputProps) {
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<AppliedPromo | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const validate = useValidatePromoCode(workspaceId);
  const isFirstRender = useRef(true);

  const isAccepted = (res: { valid: boolean; isReferral: boolean }) =>
    res.valid && !(rejectReferrals && res.isReferral);

  useEffect(() => {
    // The stash exists solely to carry a referral code to a personal
    // checkout. A form that rejects referrals must not consume it — leave it
    // for the personal PromoCodeInput the user reaches next.
    if (rejectReferrals) return;
    const pending = sessionStorage.getItem(PENDING_REFERRAL_CODE_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_REFERRAL_CODE_KEY);
    const upper = pending.toUpperCase();
    setExpanded(true);
    setCode(upper);
    validate.mutate(
      { code: upper, interval },
      {
        onSuccess: (res) => {
          if (isAccepted(res)) {
            const next: AppliedPromo = {
              code: upper,
              description: correctedDiscountDescription(res.description, interval),
              percentOff: res.percentOff,
              amountOffCents: res.amountOffCents,
            };
            setApplied(next);
            onApplied(next);
          } else {
            setRejection(REJECTION_MESSAGE[res.valid ? 'referral_team_ineligible' : res.reason ?? 'unknown_code']);
          }
        },
        onError: (err) => {
          Sentry.captureException(err);
          setRejection(VALIDATION_FAILED_MESSAGE);
        },
      },
    );
    // Runs once on mount only — the pending code is a one-shot handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!checkoutRejection) return;
    setApplied(null);
    setExpanded(true);
    onRemoved();
    setRejection(REJECTION_MESSAGE[checkoutRejection.reason]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutRejection?.nonce]);

  // A referral code valid on monthly is invalid on annual — a stale "applied"
  // badge surviving an interval switch would be a lie carried to the payment
  // attempt. Re-validate whenever the interval changes out from under an
  // applied code.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!applied) return;
    const codeToRecheck = applied.code;
    setApplied(null);
    onRemoved();
    validate.mutate(
      { code: codeToRecheck, interval },
      {
        onSuccess: (res) => {
          if (isAccepted(res)) {
            const next: AppliedPromo = {
              code: codeToRecheck,
              description: correctedDiscountDescription(res.description, interval),
              percentOff: res.percentOff,
              amountOffCents: res.amountOffCents,
            };
            setApplied(next);
            onApplied(next);
          } else {
            setExpanded(true);
            setCode(codeToRecheck);
            setRejection(REJECTION_MESSAGE[res.valid ? 'referral_team_ineligible' : res.reason ?? 'unknown_code']);
          }
        },
        onError: (err) => {
          Sentry.captureException(err);
          setExpanded(true);
          setCode(codeToRecheck);
          setRejection(VALIDATION_FAILED_MESSAGE);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);

  const handleApply = () => {
    const trimmedCode = code.trim();
    if (!trimmedCode) return;
    setRejection(null);
    validate.mutate(
      { code: trimmedCode, interval },
      {
        onSuccess: (res) => {
          if (isAccepted(res)) {
            const next: AppliedPromo = {
              code: trimmedCode,
              description: correctedDiscountDescription(res.description, interval),
              percentOff: res.percentOff,
              amountOffCents: res.amountOffCents,
            };
            setApplied(next);
            onApplied(next);
          } else {
            setRejection(REJECTION_MESSAGE[res.valid ? 'referral_team_ineligible' : res.reason ?? 'unknown_code']);
          }
        },
        onError: (err) => {
          Sentry.captureException(err);
          setRejection(VALIDATION_FAILED_MESSAGE);
        },
      },
    );
  };

  const handleRemove = () => {
    setApplied(null);
    setCode('');
    setRejection(null);
    onRemoved();
  };

  if (applied) {
    return (
      <Flex align="center" gap="sm" className={styles.applied}>
        <Text className={styles.appliedCode}>{applied.code}</Text>
        {applied.description && <Text size="sm">{applied.description}</Text>}
        <Button variant="text" size="sm" className={styles.remove} onClick={handleRemove}>
          Remove
        </Button>
      </Flex>
    );
  }

  if (!expanded) {
    return (
      <Button variant="link" size="sm" className={styles.toggle} onClick={() => setExpanded(true)}>
        Have a code?
      </Button>
    );
  }

  return (
    <Flex vertical gap="xs">
      <Flex gap="sm" className={styles.field}>
        <Input
          placeholder="Enter code"
          value={code}
          status={rejection ? 'warning' : 'default'}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
        />
        <Button variant="default" size="md" loading={validate.isPending} onClick={handleApply}>
          Apply
        </Button>
      </Flex>
      {rejection && (
        <Text size="sm" className={styles.rejection}>
          {rejection}
        </Text>
      )}
    </Flex>
  );
}
